/**
 * Theme-owned Language switcher.
 * Real languages: IT, EN (Weglot translates).
 * Display locales: FR, DE, RU, AR (English content + flag/code preference).
 */
(function () {
  const STORAGE_KEY = 'fm_display_locale';

  const LOCALES = {
    it: {
      code: 'IT',
      kind: 'real',
      flag: 'https://cdn.weglot.com/flags/square/it.svg',
    },
    en: {
      code: 'EN',
      kind: 'real',
      flag: 'https://cdn.weglot.com/flags/square/gb.svg',
    },
    fr: {
      code: 'FR',
      kind: 'display',
      flag: 'https://cdn.weglot.com/flags/square/fr.svg',
    },
    de: {
      code: 'DE',
      kind: 'display',
      flag: 'https://cdn.weglot.com/flags/square/de.svg',
    },
    ru: {
      code: 'RU',
      kind: 'display',
      flag: 'https://cdn.weglot.com/flags/square/ru.svg',
    },
    ar: {
      code: 'AR',
      kind: 'display',
      flag: 'https://cdn.weglot.com/flags/square/sa.svg',
    },
  };

  const DISPLAY_LOCALES = new Set(['fr', 'de', 'ru', 'ar']);

  function getPathRealLanguage() {
    return /^\/en(\/|$)/i.test(window.location.pathname) ? 'en' : 'it';
  }

  function getCurrentRealLanguage() {
    if (window.Weglot && typeof window.Weglot.getCurrentLang === 'function') {
      const lang = window.Weglot.getCurrentLang();
      if (lang === 'en' || lang === 'it') return lang;
    }

    const shopifyLocale = (window.Shopify && window.Shopify.locale) || '';
    const short = String(shopifyLocale).split('-')[0].toLowerCase();
    if (short === 'en' || short === 'it') return short;

    return getPathRealLanguage();
  }

  function readDisplayLocale() {
    try {
      const value = window.localStorage.getItem(STORAGE_KEY);
      return DISPLAY_LOCALES.has(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  function writeDisplayLocale(lang) {
    try {
      if (DISPLAY_LOCALES.has(lang)) {
        window.localStorage.setItem(STORAGE_KEY, lang);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      // Preference is best-effort; closed-state paint still works for this page.
    }
  }

  function clearDisplayLocale() {
    writeDisplayLocale(null);
  }

  /**
   * Closed-state resolution:
   * - Italian → clear preference, show IT
   * - English + Display locale preference → show that Display locale
   * - English + no preference → show EN
   */
  function resolveClosedLang() {
    const real = getCurrentRealLanguage();
    if (real === 'it') {
      if (readDisplayLocale()) clearDisplayLocale();
      return 'it';
    }

    return readDisplayLocale() || 'en';
  }

  function switchToEnglish() {
    if (getCurrentRealLanguage() === 'en') return false;

    if (window.Weglot && typeof window.Weglot.switchTo === 'function') {
      window.Weglot.switchTo('en');
      return true;
    }

    const path = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;
    if (/^\/en(\/|$)/i.test(path)) return false;
    const nextPath = path === '/' ? '/en' : '/en' + path;
    window.location.assign(nextPath + search + hash);
    return true;
  }

  function switchRealLanguage(lang) {
    if (lang !== 'it' && lang !== 'en') return;

    if (window.Weglot && typeof window.Weglot.switchTo === 'function') {
      window.Weglot.switchTo(lang);
      return;
    }

    const path = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;
    if (lang === 'en') {
      if (/^\/en(\/|$)/i.test(path)) return;
      const nextPath = path === '/' ? '/en' : '/en' + path;
      window.location.assign(nextPath + search + hash);
      return;
    }

    if (!/^\/en(\/|$)/i.test(path)) return;
    const nextPath = path.replace(/^\/en(?=\/|$)/i, '') || '/';
    window.location.assign(nextPath + search + hash);
  }

  function paintClosedState(root, lang) {
    const meta = LOCALES[lang];
    if (!meta) return;

    root.dataset.currentLang = lang;

    const flag = root.querySelector('[data-language-switcher-flag]');
    const code = root.querySelector('[data-language-switcher-code]');
    if (flag) flag.setAttribute('src', meta.flag);
    if (code) code.textContent = meta.code;

    root.querySelectorAll('[data-lang]').forEach((option) => {
      const selected = option.getAttribute('data-lang') === lang;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  function syncClosedState(root) {
    paintClosedState(root, resolveClosedLang());
  }

  function selectLocale(root, lang) {
    const meta = LOCALES[lang];
    if (!meta) return;

    if (meta.kind === 'real') {
      clearDisplayLocale();
      paintClosedState(root, lang);
      switchRealLanguage(lang);
      return;
    }

    // Display locale: store preference, go to English (or stay if already there).
    writeDisplayLocale(lang);
    paintClosedState(root, lang);
    switchToEnglish();
  }

  function setOpen(root, open) {
    const toggle = root.querySelector('.language-switcher__toggle');
    const list = root.querySelector('.language-switcher__list');
    if (!toggle || !list) return;

    root.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      list.removeAttribute('hidden');
    } else {
      list.setAttribute('hidden', '');
    }
  }

  function closeAll(except) {
    document.querySelectorAll('[data-language-switcher].is-open').forEach((root) => {
      if (root !== except) setOpen(root, false);
    });
  }

  function focusOption(root, lang) {
    const option = root.querySelector(`[data-lang="${lang}"]`);
    if (option) option.focus();
  }

  function optionLangs(root) {
    return Array.from(root.querySelectorAll('[data-lang]')).map((el) => el.getAttribute('data-lang'));
  }

  function initSwitcher(root) {
    const toggle = root.querySelector('.language-switcher__toggle');
    const list = root.querySelector('.language-switcher__list');
    if (!toggle || !list) return;

    syncClosedState(root);

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      const willOpen = !root.classList.contains('is-open');
      closeAll(root);
      setOpen(root, willOpen);
      if (willOpen) {
        const current = root.dataset.currentLang || resolveClosedLang();
        focusOption(root, current);
      }
    });

    toggle.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        closeAll(root);
        setOpen(root, true);
        const langs = optionLangs(root);
        const current = root.dataset.currentLang || resolveClosedLang();
        let index = langs.indexOf(current);
        if (index < 0) index = 0;
        if (event.key === 'ArrowDown') {
          index = Math.min(langs.length - 1, index + 1);
        } else {
          index = Math.max(0, index - 1);
        }
        focusOption(root, langs[index]);
      }
    });

    list.addEventListener('click', (event) => {
      const option = event.target.closest('[data-lang]');
      if (!option || !list.contains(option)) return;

      event.preventDefault();
      const lang = option.getAttribute('data-lang');
      setOpen(root, false);
      selectLocale(root, lang);
    });

    list.addEventListener('keydown', (event) => {
      const option = event.target.closest('[data-lang]');
      if (!option || !list.contains(option)) return;

      const langs = optionLangs(root);
      const index = langs.indexOf(option.getAttribute('data-lang'));

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusOption(root, langs[Math.min(langs.length - 1, index + 1)]);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusOption(root, langs[Math.max(0, index - 1)]);
      } else if (event.key === 'Home') {
        event.preventDefault();
        focusOption(root, langs[0]);
      } else if (event.key === 'End') {
        event.preventDefault();
        focusOption(root, langs[langs.length - 1]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(root, false);
        toggle.focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        option.click();
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-language-switcher]').forEach(initSwitcher);
  }

  function syncAll() {
    document.querySelectorAll('[data-language-switcher]').forEach(syncClosedState);
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-language-switcher]')) return;
    closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Weglot may finalize the Real language after first paint; re-resolve closed state
  // so Display locale preference is kept on English and cleared on Italian.
  if (window.Weglot && typeof window.Weglot.on === 'function') {
    window.Weglot.on('initialized', syncAll);
    window.Weglot.on('languageChanged', syncAll);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      const tryBind = () => {
        if (!window.Weglot || typeof window.Weglot.on !== 'function') return false;
        window.Weglot.on('initialized', syncAll);
        window.Weglot.on('languageChanged', syncAll);
        syncAll();
        return true;
      };
      if (tryBind()) return;
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (tryBind() || attempts > 40) window.clearInterval(timer);
      }, 100);
    });
  }
})();
